import React, {Fragment, useEffect, useState} from "react";
import {Link, LoaderFunctionArgs, useLoaderData} from "react-router-dom";
import axios from "axios";
import {EntityMonstrousAbility, MonstrousAbility} from "../../server/monster";
import {Autocomplete, Button, ButtonGroup, Chip, Grid, IconButton, Paper, TextField, Tooltip} from "@mui/material";
import {CreateMonstrousAbilityDialog} from "./components/CreateMonstrousAbilityDialog";
import {useMonsterCatalog} from "./contexts/MonsterCatalogContext";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import {DataGrid, GridColDef} from "@mui/x-data-grid";

interface SpeciesDetail {
    id: number
    name: string
    description: string
    monstrousAbilities: EntityMonstrousAbility[]
    Monster_Type: Array<{id: number, name: string}>
}

export const monsterSpeciesLoader = async ({params}: LoaderFunctionArgs): Promise<SpeciesDetail | null> => {
    if (params.id) {
        return axios.get(`/monsters/species/${params.id}`).then(({data}) => data)
    }
    return null
}

const typeColumns: GridColDef[] = [
    {
        field: 'name', headerName: 'Type', headerAlign: "center", flex: 1,
        renderCell: (params) => <Link to={`/monsters/type/${params.row.id}`}>{params.value}</Link>
    }
]

function abilityChipColor(type: string): 'primary' | 'success' | 'error' {
    if (type === 'IMMUNITY') return 'primary'
    if (type === 'STRENGTH') return 'success'
    return 'error'
}

export function MonsterSpeciesViewer() {
    const [species, setSpecies] = useState(useLoaderData() as SpeciesDetail)
    const [editable, setEditable] = useState(false)
    const {monstrousAbilities: catalog, refreshMonstrousAbilities} = useMonsterCatalog()
    const [selectedAbility, setSelectedAbility] = useState<MonstrousAbility | null>(null)
    const [createDialogOpen, setCreateDialogOpen] = useState(false)

    async function saveSpecies() {
        await axios.put(`/monsters/species/${species.id}`, species).then(({data}) => {
            setSpecies({...data, monstrousAbilities: species.monstrousAbilities, Monster_Type: species.Monster_Type})
            setEditable(false)
        })
    }

    useEffect(() => {
        function handleKeyDown(e: KeyboardEvent) {
            if (e.ctrlKey && e.key === 'e') { e.preventDefault(); setEditable(prev => !prev) }
            else if (e.ctrlKey && e.key === 's') { e.preventDefault(); if (editable) saveSpecies() }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [editable])

    async function addAbility() {
        if (!selectedAbility) return
        const {data} = await axios.post(`/monsters/species/${species.id}/monstrous-abilities`, {abilityId: selectedAbility.id})
        setSpecies({...species, monstrousAbilities: [...species.monstrousAbilities, data]})
        setSelectedAbility(null)
    }

    async function removeAbility(entryId: number) {
        await axios.delete(`/monsters/species/${species.id}/monstrous-abilities/${entryId}`)
        setSpecies({...species, monstrousAbilities: species.monstrousAbilities.filter(a => a.id !== entryId)})
    }

    async function handleAbilityCreated(ability: MonstrousAbility) {
        refreshMonstrousAbilities()
        const {data} = await axios.post(`/monsters/species/${species.id}/monstrous-abilities`, {abilityId: ability.id})
        setSpecies({...species, monstrousAbilities: [...species.monstrousAbilities, data]})
    }

    const assignedIds = new Set(species.monstrousAbilities.map(a => a.ability.id))
    const availableCatalog = catalog.filter(a => !assignedIds.has(a.id))

    return <Fragment>
        <Grid container spacing={1}>

            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography align="center" variant="h4" component="h4">{species.name}</Typography>
                    <Container sx={{textAlign: 'center'}}>
                        <ButtonGroup variant="contained" size="small">
                            <Button title="Edit" onClick={() => setEditable(true)} disabled={editable}><EditIcon/></Button>
                            <Button title="Save" disabled={!editable} onClick={saveSpecies}><SaveIcon/></Button>
                        </ButtonGroup>
                    </Container>
                </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Species Details</Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Name" disabled={!editable}
                                       value={species.name}
                                       onChange={(e) => setSpecies({...species, name: e.target.value})}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Description" disabled={!editable} multiline minRows={4}
                                       value={species.description ?? ''}
                                       onChange={(e) => setSpecies({...species, description: e.target.value})}/>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Monstrous Abilities</Typography>
                    <Grid container spacing={1} sx={{mt: 0.5}}>
                        <Grid item xs={7}>
                            <Autocomplete
                                options={availableCatalog}
                                getOptionLabel={(a) => `${a.name} — ${a.call} (${a.type})`}
                                value={selectedAbility}
                                onChange={(_, value) => setSelectedAbility(value)}
                                renderInput={(params) => <TextField {...params} label="Ability" size="small"/>}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <Button fullWidth variant="contained" disabled={!selectedAbility}
                                    onClick={addAbility}>Add</Button>
                        </Grid>
                        <Grid item xs={3}>
                            <Button fullWidth variant="outlined" onClick={() => setCreateDialogOpen(true)}>Create New</Button>
                        </Grid>
                        {species.monstrousAbilities.map((entry) => (
                            <Grid item xs={12} key={entry.id} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <Chip label={entry.ability.type} color={abilityChipColor(entry.ability.type)} size="small"/>
                                <Typography variant="body2" sx={{flex: 1}}>{entry.ability.name} — {entry.ability.call}</Typography>
                                <Tooltip title="Remove"><span>
                                    <IconButton size="small" color="error" onClick={() => removeAbility(entry.id as number)}>
                                        <DeleteIcon fontSize="small"/>
                                    </IconButton>
                                </span></Tooltip>
                            </Grid>
                        ))}
                        {species.monstrousAbilities.length === 0 && (
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">No monstrous abilities assigned</Typography>
                            </Grid>
                        )}
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h6" component="h6" align="center">Types</Typography>
                    <DataGrid
                        autoHeight
                        rows={species.Monster_Type ?? []}
                        columns={typeColumns}
                        getRowId={(row) => row.id}
                        density="compact"
                        initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                    />
                </Paper>
            </Grid>

        </Grid>

        <CreateMonstrousAbilityDialog
            open={createDialogOpen}
            onClose={() => setCreateDialogOpen(false)}
            onCreated={handleAbilityCreated}
        />
    </Fragment>
}
